// Serviço puro (injeção de dependências) que envia a mídia de uma mensagem do
// atendimento para uma pasta escolhida pelo usuário na árvore do AAWZ Drive do
// cliente vinculado. A árvore já vem carregada e o destino é selecionado no
// menu em cascata das ações da mensagem — sem find-or-create de pasta fixa.
// Reaproveita 100% dos endpoints já existentes — não há rota nova de backend.
//
// deps = { apiAtendimento, apiAawzDrive }
//   - apiAtendimento.buildMediaUrl(id) / apiAtendimento.fetchMediaBlob({ url })
//   - apiAawzDrive.uploadItem

const DEFAULT_FILENAME = 'arquivo'
const DEFAULT_MIME = 'application/octet-stream'
// Último recurso quando não há filename NEM mime reconhecível: nome previsível
// com extensão neutra. O backend ainda rejeita '.bin' (fora da allowlist), mas
// não regride além do 'arquivo' sem extensão atual e não quebra o fluxo.
const DEFAULT_EXTENSION = 'bin'

// Mapa mínimo mime→extensão para nomear o File quando a mensagem não traz um
// media_filename com extensão. O backend do AAWZ Drive valida uploads PELA
// EXTENSÃO do nome (allowlist servida em runtime), não pelo mime — imagens,
// stickers e áudios do WhatsApp chegam sem filename, então sem isto o nome cai
// em 'arquivo' (sem extensão) e o upload é rejeitado. Tokens escolhidos batem
// com a allowlist do backend quando o formato é suportado.
const MIME_TO_EXTENSION = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'application/pdf': 'pdf',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'docx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    'pptx',
  'text/plain': 'txt',
  'text/csv': 'csv',
  'application/zip': 'zip',
  'audio/ogg': 'ogg',
  'audio/mpeg': 'mp3',
  'audio/mp4': 'm4a',
  'audio/aac': 'aac',
  'video/mp4': 'mp4',
}

const buildError = (message, status = 400) => ({ status, message, value: null })

// Mime "puro", sem parâmetros: 'audio/ogg; codecs=opus' → 'audio/ogg'. O
// WhatsApp anexa codecs ao Content-Type das notas de voz; a chave do mapa é o
// mime sem parâmetro.
const normalizeMime = (mime) => {
  if (!mime || typeof mime !== 'string') return ''
  return mime.split(';')[0].trim().toLowerCase()
}

// Divide 'contrato.pdf' em { base: 'contrato', extension: 'pdf' }. Sem match
// (sem extensão), extension vem vazio.
const splitExtension = (name) => {
  const match = /^(.*)\.([a-z0-9]+)$/i.exec(name || '')
  if (!match) return { base: name || '', extension: '' }
  return { base: match[1], extension: match[2] }
}

// Resolve o nome final garantindo extensão reconhecível E unicidade dentro da
// pasta destino. Prioridade de extensão: (a) media_filename que já tem uma →
// usa como está; (b) derivada do media_mime_type; (c) derivada do mime do
// próprio blob (Content-Type da resposta); (d) último recurso: 'bin'.
// Um timestamp é sempre anexado ao nome-base — mesma mídia reenviada (ou duas
// mensagens sem media_filename, que caem no mesmo 'arquivo') gerava nome
// idêntico e o Drive rejeitava com 409 "já existe arquivo com este nome".
// Mesma estratégia do próprio WhatsApp ao salvar mídia baixada.
const resolveMediaFilename = ({ mensagem, blob }) => {
  const rawName = (mensagem && mensagem.media_filename) || ''
  const { base: existingBase, extension: existingExtension } =
    splitExtension(rawName)
  const timestamp = Date.now()

  if (existingExtension) {
    return existingBase + '-' + timestamp + '.' + existingExtension
  }

  const base = rawName || DEFAULT_FILENAME
  const fromMessageMime =
    MIME_TO_EXTENSION[normalizeMime(mensagem && mensagem.media_mime_type)]
  if (fromMessageMime) return base + '-' + timestamp + '.' + fromMessageMime

  const fromBlobMime = MIME_TO_EXTENSION[normalizeMime(blob && blob.type)]
  if (fromBlobMime) return base + '-' + timestamp + '.' + fromBlobMime

  return base + '-' + timestamp + '.' + DEFAULT_EXTENSION
}

// Baixa a mídia da mensagem (proxy) e a converte em File. Devolve { file } ou
// { error } com mensagem acionável.
const fetchMessageMediaFile = async ({ mensagem, apiAtendimento }) => {
  const mediaUrl = apiAtendimento.buildMediaUrl(mensagem.id)
  const mediaResp = await apiAtendimento.fetchMediaBlob({ url: mediaUrl })
  const blob = mediaResp && mediaResp.value ? mediaResp.value.blob : null
  if (!mediaResp || mediaResp.status !== 200 || !blob) {
    return {
      error: buildError(
        (mediaResp && mediaResp.message) ||
          'Não foi possível carregar a mídia da mensagem. Tente novamente.',
        (mediaResp && mediaResp.status) || 500
      ),
    }
  }
  const filename = resolveMediaFilename({ mensagem, blob })
  const file = new File([blob], filename, {
    // Prefere o mime declarado; cai no mime do próprio blob (Content-Type) e,
    // por fim, no octet-stream. O File.type é secundário aqui — o backend
    // valida pela EXTENSÃO do nome, não pelo mime.
    type: mensagem.media_mime_type || (blob && blob.type) || DEFAULT_MIME,
  })
  return { file }
}

// Troca a base do nome mantendo a extensão (com timestamp) já resolvida por
// resolveMediaFilename — nunca deixa o usuário digitar um nome sem extensão
// válida nem perder a proteção contra 409 de nome duplicado. Se o customName
// vier com sua própria extensão (o modal pré-preenche com o media_filename
// completo, ex.: "contrato.pdf"), descartamos essa extensão do usuário e
// reaplicamos a extensão canônica do arquivo — evita nome com extensão dupla
// ("contrato.pdf.pdf").
const renameWithCustomBase = (file, customName) => {
  const { extension } = splitExtension(file.name)
  const requestedBase = splitExtension(customName).base || customName
  const finalName = extension ? requestedBase + '.' + extension : requestedBase
  return new File([file], finalName, { type: file.type })
}

// Envia a mídia de uma mensagem para uma pasta JÁ ESCOLHIDA da árvore do Drive
// do cliente (a árvore já vem carregada e o usuário selecionou o destino no
// menu em cascata). Sem find-or-create de pasta fixa — a pasta chega pronta.
// customName (opcional) troca a base do nome do arquivo mantendo a extensão
// canônica resolvida por resolveMediaFilename.
export const uploadMessageMediaToFolder = async ({
  mensagem,
  idClienteApp,
  folder,
  customName,
  deps,
} = {}) => {
  const { apiAtendimento, apiAawzDrive } = deps || {}

  // Guards ANTES de montar qualquer URL/payload.
  if (!idClienteApp) {
    return buildError('Cliente não vinculado à conversa.')
  }
  if (!mensagem || !mensagem.id) {
    return buildError('Mensagem inválida para envio ao AAWZ Drive.')
  }
  if (!folder || !folder.id) {
    return buildError('Selecione uma pasta de destino no AAWZ Drive.')
  }

  const media = await fetchMessageMediaFile({ mensagem, apiAtendimento })
  if (media.error) return media.error

  const file = customName
    ? renameWithCustomBase(media.file, customName)
    : media.file

  const uploadResp = await apiAawzDrive.uploadItem({
    file,
    folder,
    rootKey: idClienteApp,
  })
  return uploadResp && uploadResp.status
    ? uploadResp
    : buildError(
        'Não foi possível enviar o arquivo para o AAWZ Drive. Tente novamente.',
        500
      )
}
